<template>
    <Head title="Incident SLA Policies" />

    <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex justify-between items-center mb-6">
            <div class="flex items-center gap-3">
                <div>
                    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Incident SLA Policies</h1>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Define service level agreements for incident response and investigation times
                    </p>
                </div>
                <InfoButton title="Managing SLA Policies" variant="default">
                    <p>
                        <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                        The Incident SLA Policies page is where you define Service Level Agreements that establish
                        time-based commitments for incident response and investigation. SLA policies ensure accountability,
                        maintain service quality, support regulatory compliance, and provide clear expectations for
                        incident handling timelines. Each policy specifies response times (how quickly triage must occur)
                        and investigation times (how long full investigations can take), with automatic breach tracking
                        and reporting when deadlines are missed.
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                        • <strong>Search:</strong> Quickly find SLA policies by name<br>
                        • <strong>Status Filter:</strong> View All, Active only, or Inactive only policies<br>
                        • <strong>SLA Digest Button:</strong> Configure automated daily/weekly email reports of SLA
                          breaches and at-risk incidents sent to managers<br>
                        • <strong>New SLA Policy:</strong> Click to create a new service level agreement with response
                          and investigation time requirements<br>
                        • <strong>Table View:</strong> See all policies with their criteria (incident types, severities),
                          response hours, investigation hours, priority, and active/inactive status<br>
                        • <strong>Edit:</strong> Click the edit icon to modify an existing SLA policy's configuration<br>
                        • <strong>Delete:</strong> Remove SLA policies that are no longer needed (only if not currently
                          assigned to incidents)<br>
                        • <strong>Toggle Status:</strong> Activate or deactivate policies - inactive policies won't be
                          applied to new incidents but preserve historical SLA tracking<br>
                        • <strong>Clear Filters:</strong> Reset search and status filters to view all policies
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                        • <strong>Set realistic timeframes</strong> - Consider team capacity, shift coverage, and
                          incident complexity<br>
                        • <strong>Align with industry standards</strong> - Reference OSHA, ISO, or sector-specific
                          requirements<br>
                        • <strong>Use priority ordering</strong> - Higher priority policies are evaluated first when
                          multiple policies match<br>
                        • <strong>Be specific with criteria</strong> - Define policies for critical incidents separately
                          from routine ones<br>
                        • <strong>Monitor breach rates</strong> - Use SLA Digest reports to track compliance and adjust
                          timeframes if needed<br>
                        • <strong>Account for non-working hours</strong> - Consider whether your organization has 24/7
                          coverage<br>
                        • <strong>Response time = triage deadline</strong> - Clock starts when incident is reported<br>
                        • <strong>Investigation time = completion deadline</strong> - Clock starts when investigation
                          begins (after triage)<br>
                        • <strong>Deactivate outdated policies</strong> - Preserves historical data while preventing
                          application to new incidents<br>
                        • <strong>Review and adjust regularly</strong> - SLA effectiveness should be evaluated quarterly
                          based on actual performance<br>
                        • Changes to SLA policies affect new incidents only - existing incidents retain their original
                          SLA requirements
                    </p>
                </InfoButton>
            </div>
            <div class="flex gap-2">
                <button
                    @click="openDigestModal"
                    class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:bg-gray-700 active:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition ease-in-out duration-150"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    SLA Digest
                </button>
                <Link
                    :href="route('settings.incidents.sla-policies.create')"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New SLA Policy
                </Link>
            </div>
        </div>

        <!-- Filters -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                    <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
                    <input
                        id="search"
                        v-model="search"
                        type="text"
                        placeholder="Search by name..."
                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                </div>

                <div>
                    <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <select
                        id="status"
                        v-model="activeFilter"
                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <div class="flex items-end">
                    <button
                        @click="clearFilters"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Policies Table -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Policy Name & Criteria
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            SLA Times
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-if="filteredPolicies.length === 0">
                        <td colspan="4" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            No SLA policies found
                        </td>
                    </tr>
                    <tr v-for="policy in filteredPolicies" :key="policy.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-6 py-4">
                            <div class="text-sm font-medium text-gray-900 dark:text-white">{{ policy.name }}</div>
                            <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                <span v-if="policy.incident_type">Type: {{ policy.incident_type.name }}</span>
                                <span v-if="!policy.incident_type" class="text-gray-400 dark:text-gray-500">Applies to all incident types</span>
                                <span v-if="policy.near_miss_only === true" class="ml-2 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs">Near Miss Only</span>
                                <span v-else-if="policy.near_miss_only === false" class="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs">Incidents Only</span>
                                <span v-else class="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full text-xs">All</span>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div v-if="policy.response_time_hours">
                                Response: {{ policy.response_time_hours }}h
                            </div>
                            <div v-if="policy.investigation_time_hours">
                                Investigation: {{ policy.investigation_time_hours }}h
                            </div>
                            <div v-if="!policy.response_time_hours && !policy.investigation_time_hours" class="text-gray-400 dark:text-gray-500">
                                No SLA times set
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span v-if="policy.is_active" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                Active
                            </span>
                            <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                Inactive
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                                :href="route('settings.incidents.sla-policies.edit', policy.id)"
                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                            >
                                Edit
                            </Link>
                            <button
                                @click="toggleStatus(policy.id)"
                                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mr-4"
                            >
                                {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                            </button>
                            <button
                                @click="confirmDelete(policy)"
                                class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div v-if="props.policies.data && props.policies.data.length > 0" class="mt-6">
            <Pagination :links="props.policies.links" />
        </div>
    </div>

    <!-- SLA Digest Modal -->
    <TransitionRoot appear :show="showDigestModal" as="template">
        <Dialog as="div" @close="closeDigestModal" class="relative z-50">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black/30 dark:bg-black/60" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700">
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
                            >
                                <div class="flex items-center">
                                    <svg class="w-6 h-6 mr-2 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Incident SLA Daily Digest Settings
                                </div>
                            </DialogTitle>

                            <div class="mt-4">
                                <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    Configure who receives daily email digests summarizing at-risk or breached incidents.
                                </p>

                                <form @submit.prevent="saveDigestSettings" class="space-y-6">
                                    <!-- Enable/Disable Toggle -->
                                    <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                        <div>
                                            <label for="digest-enabled" class="text-sm font-medium text-gray-900 dark:text-white">
                                                Enable Daily Digest
                                            </label>
                                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Send automated daily SLA digest emails
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            @click="digestForm.enabled = !digestForm.enabled"
                                            :class="[
                                                digestForm.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700',
                                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    digestForm.enabled ? 'translate-x-5' : 'translate-x-0',
                                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                ]"
                                            />
                                        </button>
                                    </div>

                                    <div v-if="digestForm.enabled" class="space-y-6">
                                        <!-- Send Time -->
                                        <div>
                                            <label for="send-time" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Send Time
                                            </label>
                                            <select
                                                id="send-time"
                                                v-model="digestForm.send_time"
                                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                required
                                            >
                                                <option value="00:00">12:00 AM (Midnight)</option>
                                                <option value="01:00">1:00 AM</option>
                                                <option value="02:00">2:00 AM</option>
                                                <option value="03:00">3:00 AM</option>
                                                <option value="04:00">4:00 AM</option>
                                                <option value="05:00">5:00 AM</option>
                                                <option value="06:00">6:00 AM</option>
                                                <option value="07:00">7:00 AM</option>
                                                <option value="08:00">8:00 AM</option>
                                                <option value="09:00">9:00 AM</option>
                                                <option value="10:00">10:00 AM</option>
                                                <option value="11:00">11:00 AM</option>
                                                <option value="12:00">12:00 PM (Noon)</option>
                                                <option value="13:00">1:00 PM</option>
                                                <option value="14:00">2:00 PM</option>
                                                <option value="15:00">3:00 PM</option>
                                                <option value="16:00">4:00 PM</option>
                                                <option value="17:00">5:00 PM</option>
                                                <option value="18:00">6:00 PM</option>
                                                <option value="19:00">7:00 PM</option>
                                                <option value="20:00">8:00 PM</option>
                                                <option value="21:00">9:00 PM</option>
                                                <option value="22:00">10:00 PM</option>
                                                <option value="23:00">11:00 PM</option>
                                            </select>
                                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Time shown in {{ timezoneInfo?.display_name || 'your company timezone' }} ({{ timezoneInfo?.gmt_offset || 'local time' }}). Digests send on the hour.
                                            </p>
                                        </div>

                                        <!-- Only if items -->
                                        <div class="flex items-center">
                                            <input
                                                id="only-if-items"
                                                v-model="digestForm.only_if_items"
                                                type="checkbox"
                                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                            />
                                            <label for="only-if-items" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Only send digest if there are at-risk or breached incidents
                                            </label>
                                        </div>

                                        <!-- Include Assigned Users -->
                                        <div class="flex items-center">
                                            <input
                                                id="include-assigned"
                                                v-model="digestForm.include_assigned"
                                                type="checkbox"
                                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                            />
                                            <label for="include-assigned" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Include users assigned to at-risk or breached incidents
                                            </label>
                                        </div>

                                        <!-- Roles Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Send to Roles
                                            </label>
                                            <div class="max-h-48 overflow-y-auto space-y-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50">
                                                <div v-for="role in availableRoles" :key="role.id" class="flex items-center">
                                                    <input
                                                        :id="`role-${role.id}`"
                                                        v-model="digestForm.roles"
                                                        :value="role.id"
                                                        type="checkbox"
                                                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                                    />
                                                    <label :for="`role-${role.id}`" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        {{ role.name }}
                                                        <span v-if="role.description" class="text-xs text-gray-500 dark:text-gray-400">
                                                            - {{ role.description }}
                                                        </span>
                                                    </label>
                                                </div>
                                                <div v-if="availableRoles.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                                    No roles available
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Users Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Send to Specific Users
                                            </label>
                                            <div class="max-h-48 overflow-y-auto space-y-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50">
                                                <div v-for="user in availableUsers" :key="user.id" class="flex items-center">
                                                    <input
                                                        :id="`user-${user.id}`"
                                                        v-model="digestForm.users"
                                                        :value="user.id"
                                                        type="checkbox"
                                                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                                    />
                                                    <label :for="`user-${user.id}`" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        {{ user.name }}
                                                        <span class="text-xs text-gray-500 dark:text-gray-400">
                                                            ({{ user.email }})
                                                        </span>
                                                    </label>
                                                </div>
                                                <div v-if="availableUsers.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                                    No users available
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Action Buttons -->
                                    <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="button"
                                            @click="closeDigestModal"
                                            class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            :disabled="isSubmittingDigest"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            v-if="digestForm.enabled"
                                            type="button"
                                            @click="sendTestDigest"
                                            class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            :disabled="isSubmittingDigest"
                                        >
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            Send Test Email
                                        </button>
                                        <button
                                            type="submit"
                                            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            :disabled="isSubmittingDigest"
                                        >
                                            <svg v-if="isSubmittingDigest" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Save Settings
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import route from '@/Utils/route';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import { useTimezone } from '@/Composables/useTimezone';
import InfoButton from '@/Components/Common/InfoButton.vue';

const page = usePage();
const toast = useToast();
const { toUTC, formatTime, timezoneInfo } = useTimezone();

const props = defineProps({
    policies: {
        type: Object,
        required: true,
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
    digestSettings: {
        type: Object,
        default: () => ({
            enabled: false,
            roles: [],
            users: [],
            include_assigned: false,
            send_time: '08:00',
            only_if_items: true,
        }),
    },
    availableRoles: {
        type: Array,
        default: () => [],
    },
    availableUsers: {
        type: Array,
        default: () => [],
    },
});

const search = ref(props.filters.search || '');
const activeFilter = ref(props.filters.active !== undefined ? (props.filters.active ? 'active' : 'inactive') : 'all');

const filteredPolicies = computed(() => {
    const policiesList = props.policies.data || [];
    return policiesList.filter(policy => {
        // Search filter
        if (search.value && !policy.name.toLowerCase().includes(search.value.toLowerCase()) &&
            !policy.description?.toLowerCase().includes(search.value.toLowerCase())) {
            return false;
        }

        // Status filter
        if (activeFilter.value === 'active' && !policy.is_active) {
            return false;
        }
        if (activeFilter.value === 'inactive' && policy.is_active) {
            return false;
        }

        return true;
    });
});

const clearFilters = () => {
    search.value = '';
    activeFilter.value = 'all';
};

const toggleStatus = (policyId) => {
    router.post(route('settings.incidents.sla-policies.toggle-active', policyId), {}, {
        preserveScroll: true,
    });
};

const confirmDelete = (policy) => {
    if (confirm(`Are you sure you want to delete "${policy.name}"? This action cannot be undone.`)) {
        router.delete(route('settings.incidents.sla-policies.destroy', policy.id), {
            preserveScroll: true,
        });
    }
};

// SLA Digest Modal
const showDigestModal = ref(false);
const isSubmittingDigest = ref(false);
const digestForm = ref({
    enabled: props.digestSettings.enabled || false,
    roles: props.digestSettings.roles || [],
    users: props.digestSettings.users || [],
    include_assigned: props.digestSettings.include_assigned || false,
    send_time: props.digestSettings.send_time || '08:00',
    only_if_items: props.digestSettings.only_if_items !== undefined ? props.digestSettings.only_if_items : true,
});

const openDigestModal = () => {
    // Reset form to current settings
    let displaySendTime = '08:00';

    // Convert UTC send time to company timezone for display
    if (props.digestSettings.send_time) {
        const utcTime = props.digestSettings.send_time; // e.g., "08:00" in UTC
        const [hours, minutes] = utcTime.split(':');

        // Create a date in UTC with the stored time
        const utcDate = new Date();
        utcDate.setUTCHours(parseInt(hours), parseInt(minutes), 0, 0);

        // Format in company timezone to get local time
        displaySendTime = formatTime(utcDate);
    }

    digestForm.value = {
        enabled: props.digestSettings.enabled || false,
        roles: props.digestSettings.roles || [],
        users: props.digestSettings.users || [],
        include_assigned: props.digestSettings.include_assigned || false,
        send_time: displaySendTime,
        only_if_items: props.digestSettings.only_if_items !== undefined ? props.digestSettings.only_if_items : true,
    };
    showDigestModal.value = true;
};

const closeDigestModal = () => {
    showDigestModal.value = false;
};

const saveDigestSettings = () => {
    isSubmittingDigest.value = true;

    // Convert company timezone to UTC before saving
    const localTime = digestForm.value.send_time; // e.g., "08:00" in company timezone
    const [hours, minutes] = localTime.split(':');

    // Create a date in company timezone with the selected time
    const localDate = new Date();
    localDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Convert to UTC
    const utcDate = toUTC(localDate);
    const utcHours = utcDate.getUTCHours().toString().padStart(2, '0');
    const utcMinutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
    const utcTime = `${utcHours}:${utcMinutes}`;

    router.post(route('settings.incidents.sla-digest.store'), {
        ...digestForm.value,
        send_time: utcTime, // Send UTC time to backend
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Digest settings saved successfully');
            closeDigestModal();
        },
        onError: () => {
            toast.error('Failed to save digest settings');
        },
        onFinish: () => {
            isSubmittingDigest.value = false;
        },
    });
};

const sendTestDigest = () => {
    isSubmittingDigest.value = true;

    router.post(route('settings.incidents.sla-digest.test'), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Test email sent successfully');
        },
        onError: () => {
            toast.error('Failed to send test email');
        },
        onFinish: () => {
            isSubmittingDigest.value = false;
        },
    });
};
</script>
